import AccessoriesApi from '@/api/accessories';
import AccessoryModel from '@/models/accessory';

const getInitialParams = () => ({
  skip: 0,
  limit: 100,
  only_deleted: false,
  order: {
    column: 'title',
    desc: true,
  },
});

export default {
  namespaced: true,

  state: {
    data: [],
    statuses: [],
    availabilityStatuses: [],
    meta: {},
    loading: false,
    inlineLoading: false,
    error: null,
    inlineError: null,
    params: getInitialParams(),
    currentItem: null,
  },

  actions: {
    async getConfig(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoriesApi.getConfig();

        if (!response.cancelled) {

          const availabilityStatuses = response.data.data.availability_statuses;
          const statuses = response.data.data.statuses;

          ctx.commit('setAvailabilityStatuses', availabilityStatuses);
          ctx.commit('setStatuses', statuses);
          ctx.commit('setMeta', response.data.meta);

          ctx.commit('setLoading', false);
        }
      } catch (err) {
        ctx.commit('setError', err);
        ctx.commit('setLoading', false);
      }
    },

    async fetch(ctx) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoriesApi.fetch(ctx.getters.params);

        ctx.commit('setData', response.data.data.items);
        ctx.commit('setMeta', response.data.meta);
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async fetchOne(ctx, id) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoriesApi.fetchOne(id);
        ctx.commit('setCurrentItem', new AccessoryModel(response.data.data));
      } catch (err) {
        ctx.commit('setError', err);
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async save(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoriesApi.update(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async inlineSave(ctx, data) {
      ctx.commit('setInlineLoading', true);
      ctx.commit('setInlineError', null);

      try {
        const response = await AccessoriesApi.update(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setInlineError', err);
        return err;
      } finally {
        ctx.commit('setInlineLoading', false);
      }
    },

    async remove(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        await AccessoriesApi.remove(data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async create(ctx, data) {
      ctx.commit('setLoading', true);
      ctx.commit('setError', null);

      try {
        const response = await AccessoriesApi.create(data);
        ctx.commit('setCurrentItem', response.data.data);
        return null;
      } catch (err) {
        ctx.commit('setError', err);
        return err;
      } finally {
        ctx.commit('setLoading', false);
      }
    },

    async changeParams(ctx, params, reset) {
      if (reset) {
        ctx.commit('changeParams', {
          ...getInitialParams(),
          ...params,
        });
      } else {
        ctx.commit('changeParams', params);
      }
    },

    clearCurrentItem(ctx) {
      ctx.commit('clearCurrentItem');
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },

    setInlineLoading(state, value) {
      state.inlineLoading = value;
    },

    setError(state, err) {
      state.error = err;
    },

    setInlineError(state, err) {
      state.inlineError = err;
    },

    setAvailabilityStatuses(state, data) {
      state.availabilityStatuses = data;
    },

    setData(state, data) {
      state.data = data;
    },

    setMeta(state, meta) {
      state.meta = meta;
    },

    setStatuses(state, data) {
      state.statuses = data;
    },

    changeParams(state, params) {
      Object.assign(state.params, params);
    },

    setCurrentItem(state, data) {
      state.currentItem = data;
    },

    clearCurrentItem(state, data) {
      state.currentItem = null;
    },
  },

  getters: {
    meta: state => state.meta,
    list: state => state.data.sort((a, b) => {
      return a.title.localeCompare(b.title)
    }),
    statuses: state => state.statuses,
    availabilityStatuses: state => state.availabilityStatuses,
    error: state => state.error,
    inlineError: state => state.inlineError,
    params: (state, getters) => {
      return {
        ...state.params,
        search_string: getters.searchQuery,
      };
    },
    loading: state => state.loading,
    inlineLoading: state => state.inlineLoading,
    searchQuery: (state, getters, rootState, rootGetters) => rootGetters['util/searchQuery'],
    currentItem: state => state.currentItem,
  },
};
